<script setup>
import { ref, onMounted, computed } from 'vue';
import { useToast } from '@/composables/use-toast';
import axios from '@/plugins/axios';
import { uploadDatasetFile, deleteDatasetFile } from '@/services/api';
import Alert from '@/components/Alert.vue';
import { v4 as uuidv4 } from 'uuid';

const { addToast } = useToast();

onMounted(async () => {
  await fetchFiles();
});

const showDeleteAlert = ref(false);
const showModal = ref(false);
const newFileName = ref('');
const newFileExtension = ref('');
const newFileSize = ref('');
const rowData = ref([]);
const searchQuery = ref('');
const isEditing = ref(false);
const editingFileName = ref(null);
const showFilePreview = ref(false);
const isLoading = ref(false);
const downloadingFileName = ref(null);
const showFileNameAlert = ref(false);
const fileNameAlertData = ref(null);
const expandedFolders = ref([]);
const fileToDelete = ref(null);
const previewMode = ref('preview');
const selectedFileInfo = ref({
    name: 'example.parquet',
    size: 1024000,
    type: 'parquet',
    owner: 'Untitled User',
    columns: [
        { name: 'id', type: 'integer' },
        { name: 'name', type: 'string' },
        { name: 'size', type: 'number' },
        { name: 'extension', type: 'string' },
        { name: 'type', type: 'string' },
        { name: 'owner', type: 'string' },
        { name: 'actions', type: 'string' },
    ],
    rows: 10000
});
const formData = ref({});
const fileInput = ref(null);

const columns = ref([
  {
    key: 'name',
    label: 'Name',
  },
  {
    key: 'size',
    label: 'Size',
  },
  {
    key: 'extension',
    label: 'Extension',
  },
  {
    key: 'dataset_type',
    label: 'Type',
  },
  {
    key: 'owner',
    label: 'Owner',
  },
  {
    key: 'actions',
    label: 'Actions',
  },
]);

const getFilteredFiles = (files, query) => {
    if (!query) return files;
    const lowerQuery = query.toLowerCase();
    return files.filter(file => {
        return file.name?.toLowerCase().includes(lowerQuery) ||
               file.extension?.toLowerCase().includes(lowerQuery) ||
               file.folder?.toLowerCase().includes(lowerQuery) ||
               file.dataset_type?.toLowerCase().includes(lowerQuery) ||
               file.owner?.username?.toLowerCase().includes(lowerQuery);
    });
};

const getOrCreateOwnerGroup = (ownerGroups, ownerKey, owner) => {
    if (!ownerGroups.has(ownerKey)) {
        ownerGroups.set(ownerKey, {
            id: `owner-${ownerKey}`,
            type: 'owner-group',
            name: owner.username,
            profile_pic: owner.profile_pic,
            folders: new Map(),
            totalFiles: 0,
            owner: owner
        });
    }
    return ownerGroups.get(ownerKey);
};

const getOrCreateFolder = (ownerKey, currentLevel, currentPath, part, partIndex, pathParts, owner) => {
    if (!currentLevel.has(currentPath)) {
        currentLevel.set(currentPath, {
            id: `folder-${ownerKey}-${currentPath}`,
            type: 'folder',
            name: part,
            fullPath: currentPath,
            fileCount: 0,
            files: [],
            subfolders: new Map(),
            owner: owner,
            parentPath: partIndex > 0 ? pathParts.slice(0, partIndex).join('/') : null,
            ownerKey: ownerKey
        });
    }
    return currentLevel.get(currentPath);
};

const addFileToFolder = (file, folder, ownerGroup, index) => {
    folder.files.push({
        ...file,
        id: `file-${index}`,
        type: 'file'
    });
    folder.fileCount++;
    ownerGroup.totalFiles++;
};

const organizeFileIntoFolders = (file, index, ownerGroups, rootFiles) => {
    if (!file.folder || file.folder.trim() === '') {
        rootFiles.push({
            ...file,
            id: `file-${index}`,
            type: 'file',
            folder: ''
        });
        return;
    }

    const owner = { 
        username: file.owner?.username || 'Unknown', 
        profile_pic: file.owner?.profile_pic || '-' 
    };
    const ownerKey = owner.username;
    const ownerGroup = getOrCreateOwnerGroup(ownerGroups, ownerKey, owner);
    
    const folderPath = file.folder;
    const pathParts = folderPath.split('/').filter(part => part.trim() !== '');
    
    let currentLevel = ownerGroup.folders;
    let currentPath = '';
    
    pathParts.forEach((part, partIndex) => {
        currentPath = currentPath ? `${currentPath}/${part}` : part;
        const folder = getOrCreateFolder(ownerKey, currentLevel, currentPath, part, partIndex, pathParts, owner);
        
        const isLastPart = partIndex === pathParts.length - 1;
        if (isLastPart) {
            addFileToFolder(file, folder, ownerGroup, index);
        }
        
        currentLevel = folder.subfolders;
    });
};

const buildFolderData = (folder, ownerGroupId, level, parentId) => {
    const folderUUID = ownerGroupId + '-' + uuidv4();
    return {
        ...folder,
        id: folderUUID,
        level: level,
        parentId: parentId,
        children: [],
        folderCount: 0,
        fileCount: 0
    };
};

const addFileChildrenToFolderData = (folderData, level) => {
    folderData.files.forEach(file => {
        folderData.children.push({
            ...file,
            id: folderData.id + '-' + uuidv4(),
            level: level + 1,
            parentId: folderData.id,
            fileCount: 1,
            folderCount: 0
        });
    });
    folderData.folderCount = folderData.children.length;
    folderData.fileCount = folderData.children.reduce((acc, child) => acc + child.fileCount, 0);
};

const addFoldersToResult = (folders, ownerGroupId, parentId, level, result) => {
    folders.forEach(folder => {
        const folderData = buildFolderData(folder, ownerGroupId, level, parentId);
        addFileChildrenToFolderData(folderData, level);
        
        result.push(folderData);
        
        const hasSubfolders = folder.subfolders && folder.subfolders.size > 0;
        folderData.folderCount = folder.subfolders?.size || 0;
        if (hasSubfolders) {
            addFoldersToResult(folder.subfolders, ownerGroupId, folderData.id, level + 1, result);
        }
    });
};

const organizedData = computed(() => {
    const ownerGroups = new Map();
    const rootFiles = [];
    
    const filteredFiles = getFilteredFiles(rowData.value, searchQuery.value);
    
    filteredFiles.forEach((file, index) => {
        organizeFileIntoFolders(file, index, ownerGroups, rootFiles);
    });
    
    const result = [];
    
    ownerGroups.forEach(ownerGroup => {
        const ownerGroupId = uuidv4();
        result.push({
            ...ownerGroup,
            children: [],
            id: ownerGroupId,
            folderCount: ownerGroup.folders.size || 0
        });
        
        addFoldersToResult(ownerGroup.folders, ownerGroupId, ownerGroupId, 1, result);
    });
    
    rootFiles.forEach(file => {
        result.push({
            ...file,
            id: uuidv4(),
            level: 0,
            parentId: null,
            folderCount: 0
        });
    });
    
    return result;
});

const toggleFolder = (itemId) => {
    const index = expandedFolders.value.indexOf(itemId);
    if (index === -1) {
        expandedFolders.value.push(itemId);
    } else {
        expandedFolders.value.splice(index, 1);
    }
};

const toggleAllFolders = (value = true) => {
  const expandableItems = organizedData.value.filter(item => 
      item.type === 'owner-group' || item.type === 'folder'
  );
  if(expandedFolders.value.length === expandableItems.length && value) {
    expandedFolders.value = [];
  } else {
    expandedFolders.value = expandableItems.map(item => item.id);
  }
};

const fetchFiles = async () => {
  try {
      const response = await axios.get('/api/files/get_all_files');
      if(response.status === 200) {
          rowData.value = response.data.map(file => ({
              name: formatFileName(file.name),
              extension: file.extension,
              folder: file.folder.split('/app/files/').pop(),
              dataset_type: file?.dataset_type || '-',
              owner: file?.owner || '-',
              size: formatFileSize(file.size),
          }));
      } else {
          console.error('Error fetching tags:', response.data);
      }
  } catch (error) {
      console.error('Error fetching tags:', error);
  }
};

const createFile = () => {
  isEditing.value = false;
  newFileName.value = '';
  newFileExtension.value = '';
  newFileSize.value = '';
  showModal.value = true;
};

const triggerFileUpload = () => {
  fileInput.value?.click();
};

const removeUploadedFile = () => {
  formData.value.file = null;
  formData.value.file_progress = 0;
  if (fileInput.value) {
    fileInput.value.value = '';
  }
};

const deleteFile = async (file) => {
  try {
      if(file?.name) {
          const response = await deleteDatasetFile(file.name);
          if(response.status === 200 || response.status === 201) {
              await fetchFiles();
          }
      }
  } catch (error) {
      console.error('Error deleting file:', error);
      addToast('An error occurred while deleting the file','error');
  }
};

const setupFileDownloadRequest = (file) => {
  const baseURL = axios.defaults.baseURL || 'http://localhost:3000';
  const token = localStorage.getItem('authToken');
  const headers = {
    Accept: 'application/json',
    'Content-Type': 'application/json',
  };
  if (token) headers['Authorization'] = `Bearer ${token}`;
  const filePath = file.folder ? `${file.folder}/${file.name}` : file.name;
  return {
    url: `${baseURL}/api/files/download?path=${encodeURIComponent(filePath)}`,
    headers,
  };
};

const trackDownloadProgress = async (reader, contentLength, loadingToastId) => {
  const chunks = [];
  let receivedLength = 0;
  let lastProgressUpdate = 0;
  let done = false;
  while (!done) {
    const result = await reader.read();
    done = result.done;
    if (!done) {
      chunks.push(result.value);
      receivedLength += result.value.length;
      if (contentLength) {
        const percentComplete = Math.round((receivedLength / parseInt(contentLength)) * 100);
        if (percentComplete >= lastProgressUpdate + 5) {
          lastProgressUpdate = percentComplete;
        }
      }
    }
  }
  return { chunks, receivedLength };
};

const saveDownloadedFile = (chunks, receivedLength, filename) => {
  const allChunks = new Uint8Array(receivedLength);
  let position = 0;
  for (const chunk of chunks) {
    allChunks.set(chunk, position);
    position += chunk.length;
  }
  const blob = new Blob([allChunks], { type: 'application/octet-stream' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  URL.revokeObjectURL(url);
  document.body.removeChild(link);
};

const handleDownload = async (file) => {
  if (!file?.name) return;
  if (downloadingFileName.value) {
    addToast('A download is already in progress. Please wait.', 'error');
    return;
  }
  let loadingToastId;
  try {
    downloadingFileName.value = file.name;
    const safeName = file.name.replace(/\s+/g, '_');
    const serverFilename = safeName;
    const { url, headers } = setupFileDownloadRequest(file);
    const response = await fetch(url, { method: 'GET', credentials: 'include', headers });
    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Unknown error' }));
      throw new Error(error.message || 'Download failed');
    }
    const { chunks, receivedLength } = await trackDownloadProgress(
      response.body.getReader(),
      response.headers.get('content-length'),
      loadingToastId
    );
    saveDownloadedFile(chunks, receivedLength, serverFilename);
    addToast('Download complete!', 'success');
  } catch (error) {
    console.error('Error downloading file:', error);
    if (loadingToastId) addToast.remove?.(loadingToastId);
    addToast(`Download failed: ${error.message}`, 'error');
  } finally {
    downloadingFileName.value = null;
  }
};

const closeModal = () => {
  showModal.value = false;
  newFileName.value = '';
  newFileExtension.value = '';
  newFileSize.value = '';
  isEditing.value = false;
  editingFileName.value = null;
};

const handleDelete = async (file) => {
  showDeleteAlert.value = true;
  fileToDelete.value = file;
};

const handleDeleteConfirm = async () => {
  if(fileToDelete.value) {
    await deleteFile(fileToDelete.value);
    showDeleteAlert.value = false;
    fileToDelete.value = null;
  }
};

const handleSubmit = async () => {
  closeModal();
};

const handleFileUpload = async (event, fieldKey) => {
    const file = event.target.files[0];

    if (fileInput.value) {
        fileInput.value.value = '';
    }
    
    if (!file) return;

    const allowedExtensions = ['.json', '.tar.gz', '.gz', '.zip', '.parquet'];
    const isValidExtension = allowedExtensions.some(ext => file.name.toLowerCase().endsWith(ext));
    
    if (!isValidExtension) {
        addToast('Please upload a valid file (.json, .tar.gz, .gz, .zip, .parquet)', 'error');
        return;
    }

    const uploadFile = async (fileToUpload, customName = null) => {
        try {
            isLoading.value = true;
            formData.value[`${fieldKey}_progress`] = 0;
            
            const response = await uploadDatasetFile(
                fileToUpload, 
                (progress) => {
                    const normalizedProgress = Math.min(Math.max(0, Number(progress)), 100);
                    formData.value[`${fieldKey}_progress`] = normalizedProgress;
                },
                customName
            );
                
            if (!response) {
                throw new Error('Invalid response from server');
            }

            if (response.error === 'FILE_EXISTS') {
                throw new Error('FILE_EXISTS');
            }

            formData.value[fieldKey] = {
                name: customName || fileToUpload.name,
                size: fileToUpload.size,
                extension: (customName || fileToUpload.name).split('.').pop(),
            };

            if (response.previewData) {
                formData.value[fieldKey].schema = response.previewData.schema;
                formData.value[fieldKey].columns = response.previewData.columns;
                formData.value[fieldKey].rows = response.previewData.rows;
                
                selectedFileInfo.value = {
                    name: formData.value[fieldKey].name,
                    size: formatFileSize(formData.value[fieldKey].size),
                    type: formData.value[fieldKey].extension,
                    columns: response.previewData.columns || [],
                    rows: response.previewData.rows || 0,
                    schema: response.previewData.schema || null
                };
            }

            formData.value[`${fieldKey}_progress`] = 100;
            addToast('File uploaded successfully!', 'success');

        } catch (error) {
            if (error.message === 'FILE_EXISTS') {
                const fileName = fileToUpload.name;
                const firstDotIndex = fileName.indexOf('.');
                const baseName = fileName.slice(0, firstDotIndex);
                const extension = fileName.slice(firstDotIndex + 1);
                const defaultNewName = `${baseName}.${extension}`;

                return new Promise((resolve) => {
                    showFileNameAlert.value = true;
                    fileNameAlertData.value = {
                        originalName: fileName,
                        defaultNewName,
                        resolve: async (newName) => {
                            showFileNameAlert.value = false;
                            if (!newName) {
                                addToast('Upload cancelled', 'info');
                                resolve();
                                return;
                            }

                            try {
                                await uploadFile(fileToUpload, newName);
                                resolve();
                            } catch (uploadError) {
                                if (uploadError.message === 'FILE_EXISTS') {
                                    addToast('File with this name already exists. Please try a different name.', 'error');
                                    await handleFileUpload({ target: { files: [fileToUpload] } }, fieldKey);
                                    resolve();
                                } else {
                                    throw uploadError;
                                }
                            }
                        }
                    };
                });
            } else {
                console.error('Error uploading file:', error);
                addToast(`Error uploading file: ${error.message}`, 'error');
                formData.value[fieldKey] = null;
                formData.value[`${fieldKey}_progress`] = 0;
            }
        } finally {
            isLoading.value = false;
        }
    };

    await uploadFile(file);
};

const formatFileSize = (bytes) => {
    if (!bytes && bytes !== 0) return '';
    const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
    if (bytes === 0) return '0 B';
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return `${(bytes / Math.pow(1024, i)).toFixed(2)} ${sizes[i]}`;
};

const formatFileName = (fileName) => {
    if(fileName.includes('/')) {
        return fileName.split('/')[1];
    }
    return fileName;
};
</script>

<template>
    <div class="container mx-auto px-4 py-8">
        <div class="flex justify-between items-center mb-6">
            <div>
                <h1 class="text-3xl font-bold text-gray-800 dark:text-white tracking-tight">Files</h1>
                <p class="mt-2 text-gray-600 dark:text-gray-400">Manage and organize your files</p>
            </div>
            <button 
                @click="createFile" 
                class="btn rounded-full text-white bg-gradient-to-r from-violet-600 to-violet-700 hover:from-violet-700 hover:to-violet-800 border-transparent"
            >
                <i class="mdi mdi-plus"></i> New File
            </button>
        </div>
  
      <!-- Toggle All Folders Button -->
      <div class="flex justify-start mb-6">
          <button 
              @click="toggleAllFolders"
              class="px-4 h-[36px] rounded-[16px] font-semibold text-sm text-white transition-all duration-200 flex items-center justify-center gap-3 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800"
          >
                  <i class="mdi mdi-folder-open text-md"></i>
                  {{ expandedFolders.length === organizedData.filter(item => item.type === 'folder' || item.type === 'owner-group').length ? 'Close All Folders' : 'Open All Folders' }}
          </button>
      </div>
  
        <!-- Search and Filter Section -->
        <div class="mb-6 space-y-4">
            <!-- Search Bar -->
            <div class="relative">
                <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <i class="mdi mdi-magnify text-gray-400"></i>
                </div>
                <input
                    type="text"
                    @input="toggleAllFolders(false)"
                    v-model="searchQuery"
                    placeholder="Search files..."
                    class="block w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg
                           bg-white dark:bg-gray-800 text-gray-900 dark:text-white
                           placeholder-gray-500 dark:placeholder-gray-400
                           focus:outline-none focus:ring-2 focus:ring-violet-500/50 focus:border-violet-500/50"
                />
            </div>
        </div>
  
        <!-- Modal de Criação/Edição -->
        <div v-if="showModal" class="fixed inset-0 z-50 overflow-y-auto">
            <div class="flex items-center justify-center min-h-screen px-4">
                <div class="fixed inset-0 bg-black bg-opacity-50 transition-opacity" @click="closeModal"></div>
                
                <div class="relative bg-white dark:bg-slate-900 rounded-lg shadow-xl max-w-md w-full">
                    <div class="flex justify-between items-center px-6 py-4 border-b border-gray-100 dark:border-gray-700">
                        <h2 class="text-xl font-semibold text-gray-800 dark:text-white">
                            {{ isEditing ? 'Edit File' : 'New File' }}
                        </h2>
                        <button @click="closeModal" class="text-gray-400 bg-transparent hover:bg-gray-200 hover:text-gray-900 rounded-lg text-sm p-1.5 ms-auto inline-flex items-center dark:hover:bg-gray-800 dark:hover:text-white">
                            <svg class="size-5" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                                <path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd"></path>
                            </svg>
                        </button>
                    </div>
  
                    <form @submit.prevent="handleSubmit" class="p-6 space-y-4">
                        <div>
                          <label for="fileSize" class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                              Upload File
                          </label>
                          <div class="space-y-3">
                              <input 
                                  type="file" 
                                  ref="fileInput"
                                  accept=".json,.tar.gz,.gz,.zip,.parquet"
                                  @change="(e) => handleFileUpload(e, 'file')"
                                  @click="(e) => e.target.value = ''"
                                  class="hidden"
                                  :disabled="isLoading"
                              />
                              
                              <!-- Upload Button -->
                              <button 
                                  v-if="!formData.file"
                                  @click="triggerFileUpload"
                                  :disabled="isLoading"
                                  class="w-full px-4 h-[40px] rounded-[16px] font-semibold text-sm text-white transition-all duration-200 flex items-center justify-center gap-3"
                                  :class="isLoading 
                                      ? 'bg-gray-300 dark:bg-gray-600 cursor-not-allowed' 
                                      : 'bg-gradient-to-r from-violet-600 to-violet-700 hover:from-violet-700 hover:to-violet-800'"
                              >
                                  <svg width="24" height="24" viewBox="0 0 58 58" fill="none" xmlns="http://www.w3.org/2000/svg">
                                      <circle cx="29" cy="29" r="27.5" stroke="currentColor" stroke-width="3"/>
                                      <path fill-rule="evenodd" clip-rule="evenodd" d="M29 11C29.5401 11 30.0508 11.2455 30.3882 11.6672L37.4993 20.5561C38.1127 21.3228 37.9884 22.4415 37.2217 23.0549C36.455 23.6682 35.3362 23.5439 34.7229 22.7772L30.7778 17.8458L30.7778 30.5556C30.7778 31.5374 29.9818 32.3333 29 32.3333C28.0182 32.3333 27.2222 31.5374 27.2222 30.5556L27.2222 17.8458L23.2771 22.7772C22.6637 23.5439 21.545 23.6682 20.7783 23.0549C20.0116 22.4415 19.8873 21.3228 20.5007 20.5561L27.6118 11.6672C27.9492 11.2455 28.4599 11 29 11ZM23.6667 30.5556L23.6667 28.7778H16.5556C14.5919 28.7778 13 30.3697 13 32.3333V39.4444C13 41.4081 14.5919 43 16.5556 43H41.4444C43.4081 43 45 41.4081 45 39.4444V32.3333C45 30.3697 43.4081 28.7778 41.4444 28.7778H34.3333V30.5556C34.3333 33.5011 31.9455 35.8889 29 35.8889C26.0545 35.8889 23.6667 33.5011 23.6667 30.5556ZM37.8889 34.1111C36.907 34.1111 36.1111 34.907 36.1111 35.8889C36.1111 36.8707 36.907 37.6667 37.8889 37.6667H37.9067C38.8885 37.6667 39.6844 36.8707 39.6844 35.8889C39.6844 34.907 38.8885 34.1111 37.9067 34.1111H37.8889Z" fill="white"/>
                                  </svg>
                                  {{ isLoading ? 'Uploading...' : 'Select File (.json, .tar.gz, .gz, .zip, .parquet)' }}
                              </button>
  
                              <!-- Upload Progress -->
                              <div v-if="isLoading && formData.file_progress !== undefined" class="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                                  <div 
                                      class="bg-gradient-to-r from-violet-600 to-violet-700 h-2 rounded-full transition-all duration-300"
                                      :style="{ width: formData.file_progress + '%' }"
                                  ></div>
                              </div>
  
                              <!-- Upload Status -->
                              <div v-if="formData.file" class="flex items-center gap-3 p-3 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
                                  <i v-if="formData.file_progress === 100" class="mdi mdi-check-circle text-green-500 text-xl"></i>
                                  <i v-else-if="formData.file_progress === 0 && formData.file" class="mdi mdi-alert-circle text-red-500 text-xl"></i>
                                  <i v-else class="mdi mdi-upload text-blue-500 text-xl"></i>
                                  <div class="flex-1 min-w-0">
                                      <p class="text-sm font-medium text-gray-900 dark:text-white truncate">{{ formData.file.name }}</p>
                                      <p class="text-xs text-gray-500 dark:text-gray-400">{{ formatFileSize(formData.file.size) }}</p>
                                  </div>
                                  <div class="flex gap-2">
                                      <button 
                                          @click="removeUploadedFile" 
                                          class="text-xs text-red-500 hover:text-red-600 transition-colors underline cursor-pointer"
                                      >
                                          Remove
                                      </button>
                                  </div>
                              </div>
                              
                              <!-- File Actions -->
                              <div v-if="formData.file && (formData.file.schema || formData.file.columns)" class="flex gap-2">
                                  <button 
                                      @click="() => { previewMode = 'preview'; showFilePreview = true; }"
                                      class="px-3 py-2 text-xs font-medium rounded-lg bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-300 hover:bg-blue-100 dark:hover:bg-blue-900/50 transition-colors duration-200"
                                  >
                                      Preview Schema
                                  </button>
                                  <button 
                                      @click="() => { previewMode = 'edit'; showFilePreview = true; }"
                                      class="px-3 py-2 text-xs font-medium rounded-lg bg-purple-50 dark:bg-purple-900/30 text-purple-600 dark:text-purple-300 hover:bg-purple-100 dark:hover:bg-purple-900/50 transition-colors duration-200"
                                  >
                                      Edit Schema
                                  </button>
                              </div>
                          </div>
                        </div>
  
                        <div class="flex justify-end gap-3 mt-6">
                            <button 
                                type="button"
                                @click="closeModal"
                                class="btn rounded-full bg-gray-200 dark:bg-gray-700 text-gray-500 dark:text-gray-400 hover:bg-gray-300 dark:hover:bg-gray-600 border-transparent px-4 py-1.5 text-sm"
                            >
                                Cancel
                            </button>
                            <button 
                                type="submit"
                                class="btn rounded-full text-white bg-gradient-to-r from-violet-600 to-violet-700 hover:from-violet-700 hover:to-violet-800 border-transparent px-4 py-1.5 text-sm"
                            >
                                {{ isEditing ? 'Save Changes' : 'Create File' }}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
  
        <!-- Files Table -->
        <div class="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
            <div class="max-h-[600px] overflow-y-auto">
                <table class="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                    <thead class="bg-gray-50 dark:bg-gray-700 sticky top-0 z-10">
                        <tr>
                            <th v-for="value in columns" :key="value.key" scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                {{ value.label }}
                            </th>
                        </tr>
                    </thead>
                     <tbody class="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                         <template v-for="item in organizedData" :key="item.id">
                             <!-- Owner Group Row -->
                             <tr v-if="item.type === 'owner-group'" class="bg-blue-50 dark:bg-blue-900/20 hover:bg-blue-100 dark:hover:bg-blue-900/30 transition-colors duration-200">
                                 <td class="px-6 py-4">
                                     <div class="flex items-center">
                                         <button @click="toggleFolder(item.id)" class="flex items-center text-sm font-semibold text-blue-800 dark:text-blue-200 hover:text-blue-900 dark:hover:text-blue-100">
                                             <svg 
                                                 class="w-4 h-4 mr-2 transition-transform duration-200"
                                                 :class="{ 'transform rotate-90': expandedFolders.includes(item.id) }"
                                                 xmlns="http://www.w3.org/2000/svg" 
                                                 viewBox="0 0 20 20" 
                                                 fill="currentColor"
                                             >
                                                 <path fill-rule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clip-rule="evenodd" />
                                             </svg>
                                             <i class="mdi mdi-account-group mr-2 text-blue-600 dark:text-blue-400 text-lg"></i>
                                             {{ item.name }}
                                         </button>
                                     </div>
                                 </td>
                                 <td class="px-6 py-4 text-sm text-blue-600 dark:text-blue-400 font-medium">
                                     <div class="flex items-center gap-2">
                                        {{ item.totalFiles > 0 ? item.totalFiles + ' files' : '0 files' }}
                                    </div>
                                    <div class="flex items-center gap-2">
                                        {{ item.folderCount > 0 ? item.folderCount + ' folders' : '0 folders' }}
                                    </div>
                                 </td>
                                 <td class="px-6 py-4 text-sm text-blue-600 dark:text-blue-400">
                                    <div class="flex items-center gap-2"></div>
                                 </td>
                                 <td class="px-6 py-4 text-sm text-blue-600 dark:text-blue-400">
                                    <div class="flex items-center gap-2"></div>
                                 </td>
                                 <td class="px-6 py-4 text-sm text-blue-600 dark:text-blue-400">
                                     <div class="flex items-center gap-2">
                                         <img :alt="item.owner.username" :src="item.owner.profile_pic" class="w-8 h-8 rounded-full" v-if="item.owner.profile_pic && item.owner.profile_pic !== '-'">
                                         <span class="text-sm font-medium text-blue-800 dark:text-blue-200">
                                             {{ item.owner.username }}
                                         </span>
                                     </div>
                                 </td>
                                 <td class="px-6 py-4 text-sm text-blue-600 dark:text-blue-400">
                                    <div class="flex items-center gap-2"></div>
                                 </td>
                             </tr>
                             
                             <!-- Folder Row (only show if owner group is expanded) -->
                             <template v-if="item.type === 'folder' && expandedFolders.includes(item.parentId)">
                                 <tr :id="item.id" class="bg-gray-50 dark:bg-gray-700 hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors duration-200">
                                     <td class="px-6 py-4">
                                         <div class="flex items-center" :style="{ paddingLeft: (item.level * 20) + 'px' }">
                                             <button @click="toggleFolder(item.id)" class="flex items-center text-sm font-medium text-gray-900 dark:text-white hover:text-violet-600 dark:hover:text-violet-400">
                                                 <svg 
                                                     class="w-4 h-4 mr-2 transition-transform duration-200"
                                                     :class="{ 'transform rotate-90': expandedFolders.includes(item.id) }"
                                                     xmlns="http://www.w3.org/2000/svg" 
                                                     viewBox="0 0 20 20" 
                                                     fill="currentColor"
                                                 >
                                                     <path fill-rule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clip-rule="evenodd" />
                                                 </svg>
                                                 <i class="uil uil-folder mr-2 text-yellow-500"></i>
                                                 {{ item.name }}
                                             </button>
                                         </div>
                                     </td>
                                     <td class="px-6 py-4 text-sm text-gray-500 dark:text-gray-400">
                                        <div class="flex items-center gap-2">
                                            {{ item.fileCount > 0 ? item.fileCount + ' files' : '0 files' }}
                                        </div>
                                        <div class="flex items-center gap-2">
                                            {{ item.folderCount > 0 ? item.folderCount + ' folders' : '0 folders' }}
                                        </div>
                                     </td>
                                     <td class="px-6 py-4 text-sm text-gray-500 dark:text-gray-400">
                                     </td>
                                     <td class="px-6 py-4 text-sm text-gray-500 dark:text-gray-400">
                                        <div class="flex items-center gap-2"></div>
                                     </td>
                                     <td class="px-6 py-4 text-sm text-gray-500 dark:text-gray-400">
                                         <div class="flex items-center gap-2"></div>
                                     </td>
                                     <td class="px-6 py-4 text-sm text-gray-500 dark:text-gray-400">
                                        <div class="flex items-center gap-2"></div>
                                     </td>
                                 </tr>
                                 
                                 <!-- Files within this folder (only show if folder is expanded) -->
                                 <template v-if="expandedFolders.includes(item.id)">
                                     <tr v-for="file in item.children" :key="file.id" class="hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors duration-200">
                                         <td class="px-6 py-4">
                                             <div class="flex items-center" :style="{ paddingLeft: ((item.level + 1) * 20) + 'px' }">
                                                 <i class="mdi mdi-file mr-2 text-blue-500"></i>
                                                 <span class="text-sm font-medium text-gray-900 dark:text-white">
                                                     {{ file.name }}
                                                 </span>
                                             </div>
                                         </td>
                                         <td class="px-6 py-4 text-sm text-gray-600 dark:text-gray-300">
                                             {{ file.size }}
                                         </td>
                                         <td class="px-6 py-4 text-sm text-gray-600 dark:text-gray-300">
                                             {{ file.extension }}
                                         </td>
                                         <td class="px-6 py-4 text-sm text-gray-600 dark:text-gray-300">
                                             {{ file.dataset_type }}
                                         </td>
                                         <td class="px-6 py-4 text-sm text-gray-600 dark:text-gray-300">
                                             <div class="flex items-center gap-2"></div>
                                         </td>
                                         <td class="px-6 py-4 text-sm text-gray-600 dark:text-gray-300">
                                             <div class="flex justify-center space-x-3">
                                                <button @click="handleDownload(file)"
                                                    :disabled="downloadingFileName === file.name"
                                                    class="flex items-center gap-2 px-2.5 text-gray-100 rounded-md h-7 bg-blue-500 hover:bg-blue-600 disabled:opacity-60">
                                                    <svg v-if="downloadingFileName === file.name" class="w-4 h-4 animate-spin" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                                                        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                    </svg>
                                                    <i v-else class="mdi mdi-arrow-collapse-down"></i>
                                                    {{ downloadingFileName === file.name ? 'Downloading...' : 'Download' }}
                                                </button>
                                                 <button @click="handleDelete(file)"
                                                     class="flex items-center gap-2 px-2.5 text-gray-100 rounded-md h-7 bg-red-500 hover:bg-red-600">
                                                     <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" fill="none" viewBox="0 0 24 24">
                                                         <path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                                     </svg>
                                                     Delete
                                                 </button>
                                             </div>
                                         </td>
                                         <Alert
                                           :show="showDeleteAlert"
                                           title="Are you sure you want to delete this file？"
                                           message="This action cannot be undone"
                                           type="warning"
                                           confirmText="Delete"
                                           cancelText="Cancel"
                                           @confirm="handleDeleteConfirm"
                                           @close="showDeleteAlert = false"
                                           :isLoading="isLoading"
                                           loadingText="Deleting file..."
                                         >
                                         </Alert>
                                     </tr>
                                 </template>
                             </template>
                             
                             <!-- Root Files (files not in any folder) -->
                             <tr v-if="item.type === 'file'" class="hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors duration-200">
                                 <td class="px-6 py-4">
                                     <div class="flex items-center">
                                         <i class="uis uis-file-blank mr-2 text-blue-500"></i>
                                         <span class="text-sm font-medium text-gray-900 dark:text-white">
                                             {{ item.name }}
                                         </span>
                                     </div>
                                 </td>
                                 <td class="px-6 py-4 text-sm text-gray-600 dark:text-gray-300">
                                     {{ item.size }}
                                 </td>
                                 <td class="px-6 py-4 text-sm text-gray-600 dark:text-gray-300">
                                     {{ item.extension }}
                                 </td>
                                 <td class="px-6 py-4 text-sm text-gray-600 dark:text-gray-300">
                                     {{ item.dataset_type }}
                                 </td>
                                 <td class="px-6 py-4 text-sm text-gray-600 dark:text-gray-300">
                                     <div class="flex items-center gap-2">
                                         <img :alt="item.owner.username" :src="item.owner.profile_pic" class="w-8 h-8 rounded-full" v-if="item.owner.profile_pic && item.owner.profile_pic !== '-'">
                                         <span class="text-sm font-medium text-gray-900 dark:text-white">
                                             {{ item.owner.username }}
                                         </span>
                                     </div>
                                 </td>
                                 <td class="px-6 py-4 text-sm text-gray-600 dark:text-gray-300">
                                     <div class="flex justify-center space-x-3">
                                        <button @click="handleDownload(item)"
                                            :disabled="downloadingFileName === item.name"
                                            class="flex items-center gap-2 px-2.5 text-gray-100 rounded-md h-7 bg-echoes-primary hover:bg-echoes-primary/90 disabled:opacity-60">
                                            <svg v-if="downloadingFileName === item.name" class="w-4 h-4 animate-spin" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                                                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                            </svg>
                                            <i v-else class="mdi mdi-arrow-collapse-down"></i>
                                            {{ downloadingFileName === item.name ? 'Downloading...' : 'Download' }}
                                        </button>
                                         <button @click="handleDelete(item)"
                                             class="flex items-center gap-2 px-2.5 text-gray-100 rounded-md h-7 bg-red-500 hover:bg-red-600">
                                             <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" fill="none" viewBox="0 0 24 24">
                                                 <path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                             </svg>
                                             Delete
                                         </button>
                                     </div>
                                 </td>
                                 <Alert
                                   :show="showDeleteAlert"
                                   title="Are you sure you want to delete this file？"
                                   message="This action cannot be undone"
                                   type="warning"
                                   confirmText="Delete"
                                   cancelText="Cancel"
                                   @confirm="handleDeleteConfirm"
                                   @close="showDeleteAlert = false">
                                 </Alert>
                             </tr>
                         </template>
                     </tbody>
                </table>
            </div>
        </div>
    </div>
</template>
